import { FC, useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { classNames } from 'primereact/utils';
import {
  VoxProductCatalogItem,
  VoxRoiCostConfigData,
  CostScheduleEntry,
  BeforeProviderEntry,
  VoxRoiEditSection,
} from '../../models/voxRoi';
import { validateSchedule, scheduleErrorMessage, compareByCatalogOrder, sortCatalogItems } from '../../utils/voxRoiCalc';
import { ProviderMark } from './ProviderMarks';
import styles from './VoxRoiCostRail.module.css';

export interface VoxRoiCostRailProps {
  config: VoxRoiCostConfigData;
  catalog: VoxProductCatalogItem[];
  onSave: (config: VoxRoiCostConfigData) => Promise<boolean>;
  isSaving: boolean;
  saveError: string | null;
  activeEditSection: VoxRoiEditSection;
  onEditSection: (section: VoxRoiEditSection) => void;
  onChangePassphrase: (params: { newPassphrase: string }) => Promise<void>;
  canChangePassphrase: boolean;
}

interface VendorGroup {
  vendor: string;
  items: VoxProductCatalogItem[];
}

interface BeforeProviderFormEntry {
  ordersPerLoan: string;
  cost: number | null;
}

interface CostFormValues {
  beforeProviders: Record<string, BeforeProviderFormEntry>;
  costs: Record<string, CostScheduleEntry[]>;
}

const ORDERS_PER_LOAN_ERROR = 'enter a number greater than 0';
const VALID_AMOUNT_ERROR = 'enter a valid amount';
const SCHEDULE_DATE_ERROR = 'Fix the cost dates: no gaps or overlaps';
const EMPTY_CONFIG_ERROR = 'Add at least one lender input or product cost before saving.';
const MIN_PASSPHRASE_LENGTH = 8;

const toIsoDateString = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const groupCatalogByVendor = (catalog: VoxProductCatalogItem[]): VendorGroup[] => {
  const sorted = sortCatalogItems(catalog);
  const vendorMap = new Map<string, VoxProductCatalogItem[]>();
  for (const item of sorted) {
    const existing = vendorMap.get(item.vendor);
    if (existing) {
      existing.push(item);
    } else {
      vendorMap.set(item.vendor, [item]);
    }
  }
  return Array.from(vendorMap.entries()).map(([vendor, items]) => ({ vendor, items }));
};

const latestCostOf = (schedule: CostScheduleEntry[] | undefined): number | null => {
  if (!schedule || schedule.length === 0) return null;
  const cost = schedule[schedule.length - 1].cost;
  return cost ?? null;
};

const formatCurrency = (value: number): string => `$${value.toFixed(2)}`;

const parsePositiveNumber = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  return value;
};

const ordersPerLoanError = (raw: string): string | null => {
  const value = parsePositiveNumber(raw);
  if (value === null || value <= 0) return ORDERS_PER_LOAN_ERROR;
  return null;
};

const beforeCostError = (cost: number | null): string | null => {
  if (cost === null || !Number.isFinite(cost) || cost < 0) return VALID_AMOUNT_ERROR;
  return null;
};

const scheduleCostError = (cost: number | null): string | null => {
  if (cost === null) return VALID_AMOUNT_ERROR;
  if (!Number.isFinite(cost) || cost < 0) return VALID_AMOUNT_ERROR;
  return null;
};

const buildDefaults = (config: VoxRoiCostConfigData, catalog: VoxProductCatalogItem[]): CostFormValues => {
  const costs: Record<string, CostScheduleEntry[]> = {};
  for (const item of catalog) {
    costs[item.productKey] = config.costs[item.productKey] ?? [];
  }
  const beforeProviders: Record<string, BeforeProviderFormEntry> = {};
  for (const [productKey, entry] of Object.entries(config.beforeProviders)) {
    beforeProviders[productKey] = {
      ordersPerLoan: String(entry.ordersPerLoan),
      cost: entry.cost,
    };
  }
  return { beforeProviders, costs };
};

const VoxRoiCostRail: FC<VoxRoiCostRailProps> = ({
  config,
  catalog,
  onSave,
  isSaving,
  saveError,
  activeEditSection,
  onEditSection,
  onChangePassphrase,
  canChangePassphrase,
}) => {
  const { control, handleSubmit, reset, watch, setValue, getValues, formState } = useForm<CostFormValues>({
    defaultValues: buildDefaults(config, catalog),
  });
  const { isDirty } = formState;

  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const [collapsedVendors, setCollapsedVendors] = useState<Set<string>>(
    () => new Set(catalog.map((item) => item.vendor)),
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const vendorGroups = groupCatalogByVendor(catalog);
  const catalogByKey = (() => {
    const map = new Map<string, VoxProductCatalogItem>();
    for (const item of catalog) map.set(item.productKey, item);
    return map;
  })();

  const editingLenderInputs = activeEditSection === 'lenderInputs';
  const editingCosts = activeEditSection === 'costs';

  const beforeValues = watch('beforeProviders');
  const costValues = watch('costs');

  const formErrors = (() => {
    const errors: string[] = [];

    if (editingLenderInputs) {
      for (const [productKey, entry] of Object.entries(beforeValues)) {
        const productName = catalogByKey.get(productKey)?.productDisplayName ?? productKey;
        const ordersError = ordersPerLoanError(entry.ordersPerLoan);
        if (ordersError) errors.push(`${productName}: ${ordersError}`);
        const costErr = beforeCostError(entry.cost);
        if (costErr) errors.push(`${productName}: ${costErr}`);
      }
    }

    if (editingCosts) {
      for (const [productKey, schedule] of Object.entries(costValues)) {
        const productName = catalogByKey.get(productKey)?.productDisplayName ?? productKey;
        if (schedule.length === 0) continue;
        for (const entry of schedule) {
          const costErr = scheduleCostError(entry.cost);
          if (costErr) errors.push(`${productName}: ${costErr}`);
        }
        const scheduleErr = scheduleErrorMessage(validateSchedule(schedule));
        if (scheduleErr === SCHEDULE_DATE_ERROR) {
          errors.push(`${productName}: ${scheduleErr}`);
        }
      }
    }

    return errors;
  })();

  const hasFieldErrors = formErrors.length > 0;
  const saveDisabled = !isDirty || hasFieldErrors || isSaving;

  const handleBeforeToggle = useCallback(
    (productKey: string) => {
      const current = getValues('beforeProviders');
      if (productKey in current) {
        const { [productKey]: _removed, ...rest } = current;
        setValue('beforeProviders', rest, { shouldDirty: true });
      } else {
        setValue(
          'beforeProviders',
          { ...current, [productKey]: { ordersPerLoan: '0.9', cost: null } },
          { shouldDirty: true },
        );
      }
    },
    [getValues, setValue],
  );

  const handleToggleVendor = useCallback((vendor: string) => {
    setCollapsedVendors((previous) => {
      const next = new Set(previous);
      if (next.has(vendor)) {
        next.delete(vendor);
      } else {
        next.add(vendor);
      }
      return next;
    });
  }, []);

  const handleAddEntry = useCallback(
    (productKey: string) => {
      const existing = getValues(`costs.${productKey}`) ?? [];
      const newEntry: CostScheduleEntry = { effectiveFrom: toIsoDateString(new Date()), effectiveTo: null, cost: null };
      setValue(`costs.${productKey}`, [...existing, newEntry], { shouldDirty: true });
    },
    [getValues, setValue],
  );

  const handleRemoveEntry = useCallback(
    ({ productKey, index }: { productKey: string; index: number }) => {
      const entries = (getValues(`costs.${productKey}`) ?? []).filter((_, entryIndex) => entryIndex !== index);
      setValue(`costs.${productKey}`, entries, { shouldDirty: true });
    },
    [getValues, setValue],
  );

  const cancelEdit = useCallback(() => {
    reset(buildDefaults(config, catalog));
    setValidationError(null);
    onEditSection(null);
  }, [reset, config, catalog, onEditSection]);

  const onSubmit = useCallback(
    async (values: CostFormValues) => {
      if (hasFieldErrors) return;

      for (const [productKey, schedule] of Object.entries(values.costs)) {
        if (schedule.length > 0) {
          const scheduleError = scheduleErrorMessage(validateSchedule(schedule));
          if (scheduleError) {
            setValidationError(`${catalogByKey.get(productKey)?.productDisplayName ?? productKey}: ${scheduleError}`);
            return;
          }
        }
      }

      const beforeProviders: Record<string, BeforeProviderEntry> = {};
      for (const [productKey, entry] of Object.entries(values.beforeProviders)) {
        const ordersPerLoan = parsePositiveNumber(entry.ordersPerLoan);
        if (ordersPerLoan === null || ordersPerLoan <= 0 || entry.cost === null) {
          return;
        }
        beforeProviders[productKey] = { ordersPerLoan, cost: entry.cost };
      }

      const hasAnyCostSchedule = Object.values(values.costs).some((schedule) => schedule.length > 0);
      if (Object.keys(beforeProviders).length === 0 && !hasAnyCostSchedule) {
        setValidationError(EMPTY_CONFIG_ERROR);
        return;
      }

      setValidationError(null);
      const saved = await onSave({ version: config.version, costs: values.costs, beforeProviders });
      if (saved) {
        reset(values);
      }
    },
    [onSave, config.version, catalogByKey, reset, hasFieldErrors],
  );

  const sortedCatalog = sortCatalogItems(catalog);
  const sortedBeforeSummaryKeys = Object.keys(config.beforeProviders).sort(
    compareByCatalogOrder(catalogByKey),
  );

  const selectedBeforeKeys = Object.keys(beforeValues);

  const getBeforeOrdersError = (productKey: string) => {
    if (!editingLenderInputs || !(productKey in beforeValues)) return null;
    return ordersPerLoanError(beforeValues[productKey].ordersPerLoan);
  };

  const getBeforeCostError = (productKey: string) => {
    if (!editingLenderInputs || !(productKey in beforeValues)) return null;
    return beforeCostError(beforeValues[productKey].cost);
  };

  const getScheduleEntryCostError = (productKey: string, index: number) => {
    if (!editingCosts) return null;
    const entry = costValues[productKey]?.[index];
    if (!entry) return null;
    return scheduleCostError(entry.cost);
  };

  const getScheduleDateError = (productKey: string) => {
    if (!editingCosts) return null;
    const schedule = costValues[productKey] ?? [];
    if (schedule.length === 0) return null;
    const error = scheduleErrorMessage(validateSchedule(schedule));
    return error === SCHEDULE_DATE_ERROR ? error : null;
  };

  return (
    <div className={styles.rail}>
      <form className={styles.costForm} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.railCard}>
        <div className={styles.railCardHeader}>
          <i className="pi pi-sliders-h" />
          <span className={styles.railCardTitle}>Lender Inputs</span>
          {!editingLenderInputs && (
            <Button
              type="button"
              label="Edit"
              icon="pi pi-pencil"
              text
              size="small"
              className="ml-auto"
              disabled={activeEditSection !== null}
              onClick={() => onEditSection('lenderInputs')}
              aria-label="Edit lender inputs"
            />
          )}
        </div>
        <p className={styles.railCardDescription}>
          Providers used before VoX, with each provider&rsquo;s orders per loan and cost per order.
        </p>

        {editingLenderInputs ? (
          <>
            <div className={styles.beforeProviderList}>
              {sortedCatalog.map((item) => {
                const isBefore = item.productKey in beforeValues;
                const ordersFieldId = `before-orders-${item.productKey}`;
                const costFieldId = `before-cost-${item.productKey}`;
                const ordersError = getBeforeOrdersError(item.productKey);
                const costError = getBeforeCostError(item.productKey);
                return (
                  <div key={item.productKey} className={styles.beforeProviderItem}>
                    <div className={styles.beforeProviderRow}>
                      <div className={styles.beforeCheck}>
                        <Checkbox
                          checked={isBefore}
                          onChange={() => handleBeforeToggle(item.productKey)}
                          aria-label={`Before ${item.productDisplayName}`}
                        />
                      </div>
                      <ProviderMark vendor={item.vendor} />
                      <div className={styles.beforeProviderInfo}>
                        <span className={styles.beforeProviderName}>{item.productDisplayName}</span>
                        <span className={styles.beforeProviderVendor}>{item.vendor}</span>
                      </div>
                    </div>
                    {isBefore && (
                      <div className={styles.beforeProviderFields}>
                        <div className={styles.entryField}>
                          <label htmlFor={ordersFieldId} className={styles.entryLabel}>
                            Orders / Loan for {item.productDisplayName}
                          </label>
                          <Controller
                            name={`beforeProviders.${item.productKey}.ordersPerLoan`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <InputText
                                  id={ordersFieldId}
                                  inputMode="decimal"
                                  value={field.value ?? ''}
                                  onChange={(event) => field.onChange(event.target.value)}
                                  onBlur={field.onBlur}
                                  className={classNames(styles.smallInput, { 'p-invalid': !!ordersError })}
                                />
                                {ordersError && <small className={styles.fieldError}>{ordersError}</small>}
                              </>
                            )}
                          />
                        </div>
                        <div className={styles.entryField}>
                          <label htmlFor={costFieldId} className={styles.entryLabel}>
                            Cost ($) for {item.productDisplayName}
                          </label>
                          <Controller
                            name={`beforeProviders.${item.productKey}.cost`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <InputNumber
                                  inputId={costFieldId}
                                  value={field.value}
                                  onValueChange={(event) => field.onChange(event.value ?? null)}
                                  onBlur={field.onBlur}
                                  mode="currency"
                                  currency="USD"
                                  minFractionDigits={2}
                                  maxFractionDigits={2}
                                  inputClassName={classNames(styles.costInput, { 'p-invalid': !!costError })}
                                />
                                {costError && <small className={styles.fieldError}>{costError}</small>}
                              </>
                            )}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {selectedBeforeKeys.length === 0 && (
              <div className={styles.noBeforeHint}>Check at least one provider to model your Before spend.</div>
            )}
          </>
        ) : (
          <div className={styles.summaryList}>
            {sortedBeforeSummaryKeys.length === 0 ? (
              <div className={styles.noBeforeHint}>No prior providers selected.</div>
            ) : (
              sortedBeforeSummaryKeys.map((productKey) => {
                const entry = config.beforeProviders[productKey];
                const item = catalogByKey.get(productKey);
                return (
                  <div key={productKey} className={styles.summaryRow}>
                    <ProviderMark vendor={item?.vendor ?? 'Unknown'} />
                    <span className={styles.summaryName}>{item?.productDisplayName ?? productKey}</span>
                    <span className={styles.summaryMeta}>
                      {entry.ordersPerLoan.toFixed(2)}/loan · {formatCurrency(entry.cost)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      <div className={styles.railCard}>
        <div className={styles.railCardHeader}>
          <i className="pi pi-dollar" />
          <span className={styles.railCardTitle}>Provider &amp; Product Costs</span>
          {!editingCosts && (
            <Button
              type="button"
              label="Edit"
              icon="pi pi-pencil"
              text
              size="small"
              className="ml-auto"
              disabled={activeEditSection !== null}
              onClick={() => onEditSection('costs')}
              aria-label="Edit provider and product costs"
            />
          )}
        </div>
        <p className={styles.railCardDescription}>
          Date-effective cost schedule for each product. These costs price your After (VoX) spend.
        </p>

        {editingCosts ? (
          <div className={styles.vendorGroupList}>
            {vendorGroups.map((vendorGroup) => {
              const isCollapsed = collapsedVendors.has(vendorGroup.vendor);
              return (
                <div key={vendorGroup.vendor} className={styles.vendorGroup}>
                  <button
                    type="button"
                    className={styles.vendorGroupToggle}
                    onClick={() => handleToggleVendor(vendorGroup.vendor)}
                    aria-expanded={!isCollapsed}
                  >
                    <ProviderMark vendor={vendorGroup.vendor} showName nameClassName={styles.vendorGroupName} />
                    <span className={styles.vendorGroupCount}>{vendorGroup.items.length}</span>
                    <i className={`pi ${isCollapsed ? 'pi-chevron-right' : 'pi-chevron-down'} ${styles.chevron}`} />
                  </button>

                  {!isCollapsed && (
                    <div className={styles.vendorGroupProducts}>
                      {vendorGroup.items.map((item) => {
                        const schedule = costValues[item.productKey] ?? [];
                        const isExpanded = expandedProduct === item.productKey;
                        const latestCost = latestCostOf(schedule);
                        const scheduleDateError = getScheduleDateError(item.productKey);

                        return (
                          <div key={item.productKey} className={styles.productItem}>
                            <button
                              type="button"
                              className={styles.productToggle}
                              onClick={() => setExpandedProduct(isExpanded ? null : item.productKey)}
                              aria-expanded={isExpanded}
                            >
                              <i className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} ${styles.chevron}`} />
                              <span className={styles.productName}>{item.productDisplayName}</span>
                              <span className={styles.productCostBadge}>
                                {latestCost !== null ? formatCurrency(latestCost) : '---'}
                              </span>
                            </button>

                            {isExpanded && (
                              <div className={styles.schedulePanel}>
                                {schedule.length === 0 && <div className={styles.noEntries}>No cost entries</div>}
                                {schedule.map((entry, entryIndex) => {
                                  const costFieldId = `cost-${item.productKey}-${entryIndex}`;
                                  const entryCostError = getScheduleEntryCostError(item.productKey, entryIndex);
                                  return (
                                    <div key={entryIndex} className={styles.scheduleEntry}>
                                      <div className={styles.entryRow}>
                                        <div className={styles.entryField}>
                                          <label className={styles.entryLabel} htmlFor={`from-${item.productKey}-${entryIndex}`}>From</label>
                                          <div className={styles.datePicker}>
                                            <Controller
                                              name={`costs.${item.productKey}.${entryIndex}.effectiveFrom`}
                                              control={control}
                                              render={({ field }) => (
                                                <Calendar
                                                  inputId={`from-${item.productKey}-${entryIndex}`}
                                                  value={field.value ? new Date(field.value + 'T00:00:00') : null}
                                                  onChange={(event) => {
                                                    if (event.value) field.onChange(toIsoDateString(event.value as Date));
                                                  }}
                                                  dateFormat="yy-mm-dd"
                                                  showIcon
                                                />
                                              )}
                                            />
                                          </div>
                                        </div>
                                        <div className={styles.entryField}>
                                          <label className={styles.entryLabel} htmlFor={`to-${item.productKey}-${entryIndex}`}>To</label>
                                          <div className={styles.datePicker}>
                                            <Controller
                                              name={`costs.${item.productKey}.${entryIndex}.effectiveTo`}
                                              control={control}
                                              render={({ field }) => (
                                                <Calendar
                                                  inputId={`to-${item.productKey}-${entryIndex}`}
                                                  value={field.value ? new Date(field.value + 'T00:00:00') : null}
                                                  onChange={(event) =>
                                                    field.onChange(event.value ? toIsoDateString(event.value as Date) : null)
                                                  }
                                                  dateFormat="yy-mm-dd"
                                                  showIcon
                                                  placeholder="Open-ended"
                                                />
                                              )}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                      <div className={styles.entryRow}>
                                        <div className={styles.entryField}>
                                          <label htmlFor={costFieldId} className={styles.entryLabel}>
                                            Cost ($) for {item.productDisplayName}
                                          </label>
                                          <Controller
                                            name={`costs.${item.productKey}.${entryIndex}.cost`}
                                            control={control}
                                            render={({ field }) => (
                                              <>
                                                <InputNumber
                                                  inputId={costFieldId}
                                                  value={field.value}
                                                  onValueChange={(event) => field.onChange(event.value ?? null)}
                                                  onBlur={field.onBlur}
                                                  mode="currency"
                                                  currency="USD"
                                                  minFractionDigits={2}
                                                  maxFractionDigits={2}
                                                  placeholder="Not set"
                                                  inputClassName={classNames(styles.costInput, { 'p-invalid': !!entryCostError })}
                                                />
                                                {entryCostError && <small className={styles.fieldError}>{entryCostError}</small>}
                                              </>
                                            )}
                                          />
                                        </div>
                                        <div className={styles.removeBtn}>
                                          <Button
                                            type="button"
                                            icon="pi pi-trash"
                                            severity="danger"
                                            text
                                            size="small"
                                            onClick={() => handleRemoveEntry({ productKey: item.productKey, index: entryIndex })}
                                            aria-label={`Remove cost period for ${item.productDisplayName} starting ${entry.effectiveFrom}`}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                {scheduleDateError && <small className={styles.fieldError}>{scheduleDateError}</small>}
                                <div className={styles.addBtn}>
                                  <Button
                                    type="button"
                                    label="Add period"
                                    icon="pi pi-plus"
                                    text
                                    size="small"
                                    onClick={() => handleAddEntry(item.productKey)}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.summaryList}>
            {sortedCatalog.filter((item) => latestCostOf(config.costs[item.productKey]) !== null).length === 0 ? (
              <div className={styles.noBeforeHint}>No product costs configured yet.</div>
            ) : (
              sortedCatalog
                .filter((item) => latestCostOf(config.costs[item.productKey]) !== null)
                .map((item) => (
                  <div key={item.productKey} className={styles.summaryRow}>
                    <ProviderMark vendor={item.vendor} />
                    <span className={styles.summaryName}>{item.productDisplayName}</span>
                    <span className={styles.summaryMeta}>{formatCurrency(latestCostOf(config.costs[item.productKey]) as number)}</span>
                  </div>
                ))
            )}
          </div>
        )}
      </div>

      {validationError && <Message severity="error" text={validationError} className="w-full" />}
      {saveError && <Message severity="error" text={saveError} className="w-full" />}

      {activeEditSection !== null && (
        <div className={styles.railActions}>
          <Button type="submit" label="Save" icon="pi pi-check" loading={isSaving} disabled={saveDisabled} size="small" />
          <Button
            type="button"
            label="Cancel"
            icon="pi pi-times"
            severity="secondary"
            outlined
            onClick={cancelEdit}
            disabled={isSaving}
            size="small"
          />
        </div>
      )}
      </form>

      {canChangePassphrase && <ChangePassphrase onChangePassphrase={onChangePassphrase} />}
    </div>
  );
};

const ChangePassphrase: FC<{ onChangePassphrase: (params: { newPassphrase: string }) => Promise<void> }> = ({ onChangePassphrase }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newPassphrase, setNewPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasError = !!error;
  const trimmedNew = newPassphrase.trim();
  const trimmedConfirm = confirmPassphrase.trim();
  const meetsLength = trimmedNew.length >= MIN_PASSPHRASE_LENGTH;
  const canSubmit =
    trimmedNew.length > 0 &&
    trimmedConfirm.length > 0 &&
    meetsLength &&
    trimmedNew === trimmedConfirm &&
    !isSubmitting;

  const reset = useCallback(() => {
    setNewPassphrase('');
    setConfirmPassphrase('');
    setShowPassword(false);
    setError(null);
    setIsSubmitting(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);
    if (!trimmedNew) {
      setError('Enter a new passphrase.');
      return;
    }
    if (trimmedNew.length < MIN_PASSPHRASE_LENGTH) {
      setError(`Use at least ${MIN_PASSPHRASE_LENGTH} characters.`);
      return;
    }
    if (trimmedNew !== trimmedConfirm) {
      setError('Passphrases do not match.');
      return;
    }
    setIsSubmitting(true);
    try {
      await onChangePassphrase({ newPassphrase: trimmedNew });
      reset();
      setIsOpen(false);
    } catch {
      setError('Failed to change passphrase. Your existing passphrase is unchanged.');
      setIsSubmitting(false);
    }
  }, [trimmedNew, trimmedConfirm, onChangePassphrase, reset]);

  if (!isOpen) {
    return (
      <div className={styles.railActions}>
        <Button type="button" label="Change passphrase" icon="pi pi-key" text size="small" onClick={() => setIsOpen(true)} />
      </div>
    );
  }

  return (
    <div className={styles.changePassphrase}>
      <span className={styles.railCardTitle}>Change passphrase</span>
      <Message
        severity="warn"
        text="Mezzo cannot recover a lost passphrase. If you forget the new passphrase, your saved costs become permanently unreadable."
      />
      <label htmlFor="vox-change-passphrase-new" className={styles.entryLabel}>New passphrase</label>
      <div className={styles.passwordInputRow}>
        <InputText
          id="vox-change-passphrase-new"
          type={showPassword ? 'text' : 'password'}
          value={newPassphrase}
          onChange={(event) => setNewPassphrase(event.target.value)}
          className={classNames({ 'p-invalid': hasError })}
          aria-invalid={hasError}
          autoComplete="new-password"
        />
        <Button
          type="button"
          label="Show Password"
          text
          size="small"
          aria-pressed={showPassword}
          onClick={() => setShowPassword((previous) => !previous)}
        />
      </div>
      {trimmedNew.length > 0 && !meetsLength && (
        <small className={styles.fieldError}>Use at least {MIN_PASSPHRASE_LENGTH} characters.</small>
      )}
      <label htmlFor="vox-change-passphrase-confirm" className={styles.entryLabel}>Confirm new passphrase</label>
      <InputText
        id="vox-change-passphrase-confirm"
        type={showPassword ? 'text' : 'password'}
        value={confirmPassphrase}
        onChange={(event) => setConfirmPassphrase(event.target.value)}
        className={classNames({ 'p-invalid': hasError })}
        aria-invalid={hasError}
        autoComplete="new-password"
      />
      {error && <Message severity="error" text={error} />}
      <div className={styles.railActions}>
        <Button type="button" label="Update passphrase" icon="pi pi-check" size="small" onClick={handleSubmit} loading={isSubmitting} disabled={!canSubmit} />
        <Button type="button" label="Cancel" icon="pi pi-times" severity="secondary" outlined size="small" disabled={isSubmitting} onClick={() => { reset(); setIsOpen(false); }} />
      </div>
    </div>
  );
};

export default VoxRoiCostRail;
